<template>
  <div class="period-date-select">
    <strong>{{ $t(this.periodLabel) }}</strong>
    <b-row class="period-date-select__form-wrapper">
      <b-col
        class="period-date-select__range mb-3 mb-lg-0"
        xl="3"
        lg="4"
        md="12"
      >
        <b-form-radio-group
          :checked="period"
          @input="$emit('update:period', $event)"
          @change="onChangePeriod"
          :options="options"
          stacked
        />
      </b-col>
      <b-col
        v-if="personalized"
        class="period-date-select__dates"
        xl="5"
        lg="7"
        md="12"
      >
        <b-row class="period-date-select__date mb-3 mb-lg-2">
          <b-col
            class="period-date-select__initial-date"
            lg="6"
            md="12"
          >
            <strong>{{ $t('fields.general.initial-date') }}</strong>
            <s-input-date-picker
              :boundary="'window'"
              :value="initialDate"
              :disabled="!isPersonalizedPeriod"
              :time-zone="timeZone"
              @input="onChangeInitialDate"
              @lazy-blur-native-proxy="validateDate(initialDateTime,
                'initial-date', onInvalidMinDate, onInvalidDate)"
            />
          </b-col>
          <b-col
            class="period-date-select__initial-time"
            lg="6"
            md="12"
          >
            <strong>{{ $t('fields.general.initial-time') }}</strong>
            <s-input-time
              v-model="initialTimeProxy"
              :disabled="!isPersonalizedPeriod"
              :required="true"
              step="1"
              @input="onChangeTime"
              @blur-native-proxy="onBlurTime(initialDateTime, 'initial-time')"
            />
          </b-col>
        </b-row>
        <b-row class="period-date-select__time">
          <b-col
            class="period-date-select__final-date"
            lg="6"
            md="12"
          >
            <strong>{{ $t('fields.general.final-date') }}</strong>
            <s-input-date-picker
              :boundary="'window'"
              :value="finalDate"
              :disabled="!isPersonalizedPeriod"
              :time-zone="timeZone"
              @input="onChangeFinalDate"
              @lazy-blur-native-proxy="validateDate(finalDateTime,
                'final-date', onInvalidMinDate, onInvalidDate)"
            />
          </b-col>
          <b-col
            class="period-date-select__final-time"
            lg="6"
            md="12"
          >
            <strong>{{ $t('fields.general.final-time') }}</strong>
            <s-input-time
              v-model="finalTimeProxy"
              :disabled="!isPersonalizedPeriod"
              :required="true"
              step="1"
              @input="onChangeTime"
              @blur-native-proxy="onBlurTime(finalDateTime, 'final-time')"
            />
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  formatter as dateTimeFormatter,
  getStartOfDay,
} from '@/services/dateTime/datetimeService';
import validateDateMixin from '@/components/mixins/validatePeriodDateMixin';
import periods from '@/domain/enums/periods';
import periodsDropdownFilter from '@/domain/enums/periods-dropdown-filter';

export default {
  name: 'PeriodDateSelect',
  props: {
    periodLabel: {
      type: String,
      default: 'reports.period.select-range',
    },
    horizontalArrangement: {
      type: Boolean,
      default: true,
    },
    initialDate: {
      type: String,
      default: '',
    },
    initialTime: {
      type: String,
      default: '',
    },
    finalDate: {
      type: String,
      default: '',
    },
    finalTime: {
      type: String,
      default: '',
    },
    period: {
      type: Number,
      default: periods.SEVEN,
    },
    additionalPeriods: {
      type: Array,
      default: () => ([]),
    },
    personalized: {
      type: Boolean,
      default: true,
    },
    hasAllPeriods: {
      type: Boolean,
      default: false,
    },
    invalid: {
      type: Boolean,
      default: false,
    },
    timeZone: {
      type: String || null,
      default: null,
    },
  },
  mixins: [
    validateDateMixin,
  ],
  data() {
    const additionalData = [];

    if (this.additionalPeriods.length) {
      this.additionalPeriods.forEach((periodItem) => {
        additionalData.push({
          text: this.$t('reports.period.last-days', { days: periodItem }),
          value: periodItem,
        });
      });
    }

    if (this.personalized) {
      additionalData.push(
        { text: this.$t('reports.period.personalized'), value: periods.PERSONALIZED },
      );
    }

    if (this.hasAllPeriods) {
      additionalData.push(
        { text: this.$t('reports.period.all'), value: periodsDropdownFilter.ALL },
      );
    }

    return {
      options: [
        {
          text: this.$t('reports.period.last-days', { days: periods.SEVEN }),
          value: periods.SEVEN,
        },
        {
          text: this.$t('reports.period.last-days', { days: periods.FIFTEEN }),
          value: periods.FIFTEEN,
        },
        {
          text: this.$t('reports.period.last-days', { days: periods.THIRTY }),
          value: periods.THIRTY,
        },
        ...additionalData,
      ],
    };
  },
  computed: {
    initialTimeProxy: {
      get() {
        return this.initialTime;
      },
      set(value) {
        this.$emit('update:initial-time', value);
        this.$emit('update:initialDateTime', this.mountDateTime(this.initialDate, value));
      },
    },
    finalTimeProxy: {
      get() {
        return this.finalTime;
      },
      set(value) {
        this.$emit('update:final-time', value);
        this.$emit('update:finalDateTime', this.mountDateTime(this.finalDate, value));
      },
    },
    isPersonalizedPeriod() {
      return this.period === periods.PERSONALIZED;
    },
  },
  methods: {
    mountDateTime(date, time) {
      return this.addTimeToDate(date, time, this.timeZone);
    },
    getPeriodFieldLabel(field) {
      const fields = {
        'final-date': this.$t('fields.general.final-date'),
        'final-time': this.$t('fields.general.final-time'),
        'initial-time': this.$t('fields.general.initial-time'),
        'initial-date': this.$t('fields.general.initial-date'),
      };
      return fields[field];
    },
    onInvalidDate() {
      this.$pushAlert({
        code: 'MA006',
      });
      this.$emit('update:invalid', true);
    },
    onInvalidMinDate(field) {
      this.$pushAlert({
        code: 'MA038',
        messageParams: {
          NomeDoCampo: this.getPeriodFieldLabel(field),
          FaixaInicial: dateTimeFormatter(this.minDate),
        },
      });
      this.$emit('update:invalid', true);
    },
    onChangeInitialDate(value) {
      this.onChangeDate(value, 'initial');
    },
    onChangeFinalDate(value) {
      this.onChangeDate(value, 'final');
    },
    onChangeDate(value, field) {
      this.$emit(`update:${field}Date`, value);
      this.$emit(`update:${field}DateTime`, this.mountDateTime(value, this[`${field}Time`]));

      this.$emit('update:date');
      this.$emit('update:dateTime');
    },
    onChangePeriod(period) {
      this.fillDateIfPersonalizedPeriod(period);
    },
    onChangeTime() {
      this.$emit('update:time');
    },
    onBlurTime(value, field) {
      this.validateDate(value, field, this.onInvalidMinDate, this.onInvalidDate);
      if (field === 'final-time') {
        this.$emit('update:finalTime', this.finalTime || '00:00');
        this.$emit('update:finalDateTime', this.mountDateTime(this.finalDate, this.finalTime));
        return;
      }
      this.$emit('update:initialTime', this.initialTime || '00:00');
      this.$emit('update:initialDateTime', this.mountDateTime(this.initialDate, this.initialTime));
    },
    fillDefaultDates() {
      const opts = { timeZone: this.timeZone };
      const today = getStartOfDay(opts);
      this.$emit('update:initialDate', today);
      this.$emit('update:initialDateTime', this.mountDateTime(today, '00:00:00'));
      this.$emit('update:initialTime', '00:00:00');

      this.$emit('update:finalDate', today);
      this.$emit('update:finalDateTime', this.mountDateTime(today, '23:59:59'));
      this.$emit('update:finalTime', '23:59:59');
    },
    resetDates() {
      this.$emit('update:invalid', false);

      this.$emit('update:initialDate', undefined);
      this.$emit('update:initialDateTime', undefined);
      this.$emit('update:initialTime', undefined);

      this.$emit('update:finalDate', undefined);
      this.$emit('update:finalDateTime', undefined);
      this.$emit('update:finalTime', undefined);
    },
    fillDateIfPersonalizedPeriod(period) {
      this.$emit('update:period', period);
      if (period === periods.PERSONALIZED) {
        this.fillDefaultDates();
        return;
      }
      this.resetDates();
    },
    emitDefaultValue() {
      this.$emit('update:period', this.period);
    },
  },
  created() {
    this.emitDefaultValue();
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/variables"

.period-date-select
  &__range
    ::v-deep.custom-radio
      margin-top: 10px

  ::v-deep.form-control:disabled
    color: $color-gray-intermediary !important

  ::v-deep
    input[type="time"]::-webkit-clear-button,
    input[type="time"]::-ms-clear
      display: none
      background: none

</style>
